import { captureException } from '@sentry/react';
import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { useCallback } from 'react';
import type {
  LocationPaymentEvent,
  TerminalStateEvent,
  TerminalWithLatestPaymentResponse,
} from '@ordr-as/payment-service-types/epos';
import { LocationPaymentEventState, TerminalStateEventLatestState } from '@ordr-as/payment-service-types/epos';
import type { CartDto } from '../../generated-api';
import { useHasActiveRefund } from '../hooks/useActiveRefunds';
import { terminalStatesStoreKey } from '../constants/storage-keys';

// === Types ===

export type TerminalDisplayState = 'AVAILABLE' | 'BUSY' | 'OFFLINE';

export type PaymentTrackingState = 'INITIATING' | 'AWAITING_PAYMENT' | 'AUTHORISING' | 'AUTHORISED' | 'FAILED';

export interface TrackedPayment {
  paymentReference: string;
  terminalId: string;
  state: PaymentTrackingState;
  timestampMs: number;
  cart?: CartDto;
}

export interface TerminalEntry {
  hardwareState: TerminalStateEventLatestState;
  activePayment: TrackedPayment | null;
  lastUpdatedMs: number;
}

export type SseConnectionState = 'disconnected' | 'connecting' | 'connected';

// === State Machine ===

const STATE_ORDINAL: Record<PaymentTrackingState, number> = {
  AUTHORISED: 3,
  AUTHORISING: 2,
  AWAITING_PAYMENT: 1,
  FAILED: 3,
  INITIATING: 0,
};

export function isPaymentInProgress(state: PaymentTrackingState): boolean {
  return STATE_ORDINAL[state] < STATE_ORDINAL['AUTHORISED'];
}

function mapLocationPaymentState(state: LocationPaymentEventState): PaymentTrackingState | null {
  switch (state) {
    case LocationPaymentEventState.INITIATING: {
      return 'INITIATING';
    }
    case LocationPaymentEventState.AWAITING_PAYMENT: {
      return 'AWAITING_PAYMENT';
    }
    case LocationPaymentEventState.AUTHORISING: {
      return 'AUTHORISING';
    }
    case LocationPaymentEventState.AUTHORISED: {
      return 'AUTHORISED';
    }
    case LocationPaymentEventState.FAILED: {
      return 'FAILED';
    }
    default: {
      return null;
    }
  }
}

function mapPollingPaymentState(state: string): PaymentTrackingState | null {
  switch (state) {
    case 'INITIATING': {
      return 'INITIATING';
    }
    case 'AWAITING_PAYMENT': {
      return 'AWAITING_PAYMENT';
    }
    case 'AUTHORISING': {
      return 'AUTHORISING';
    }
    case 'AUTHORISED':
    case 'CAPTURING':
    case 'CAPTURED': {
      return 'AUTHORISED';
    }
    case 'FAILED': {
      return 'FAILED';
    }
    default: {
      return null;
    }
  }
}

function mapHardwareToDisplay(state: TerminalStateEventLatestState | undefined): TerminalDisplayState {
  switch (state) {
    case TerminalStateEventLatestState.OK: {
      return 'AVAILABLE';
    }
    case TerminalStateEventLatestState.BUSY: {
      return 'BUSY';
    }
    case TerminalStateEventLatestState.UNREACHABLE:
    case TerminalStateEventLatestState.UNKNOWN:
    case TerminalStateEventLatestState.MAINTENANCE:
    case TerminalStateEventLatestState.OUT_OF_ORDER: {
      return 'OFFLINE';
    }
    default: {
      return 'AVAILABLE';
    }
  }
}

// === Optimistic busy (outside Zustand state to keep timers out of the store) ===

const optimisticBusyTimers = new Map<string, ReturnType<typeof setTimeout>>();

const OPTIMISTIC_BUSY_TIMEOUT_MS = 15_000;
const TRACKED_PAYMENT_GRACE_MS = 60_000;

// === Subscriber system (outside Zustand state to avoid re-renders) ===

type PaymentCallback = (state: PaymentTrackingState) => void;
const paymentSubscribers = new Map<string, Set<PaymentCallback>>();

function notifySubscribers(paymentReference: string, state: PaymentTrackingState) {
  const subs = paymentSubscribers.get(paymentReference);
  if (!subs) {
    return;
  }
  for (const cb of subs) {
    try {
      cb(state);
    } catch (error) {
      captureException(error);
    }
  }
}

// === Persistence ===

function loadPersistedHardwareStates(): Record<string, TerminalStateEventLatestState> {
  try {
    const stored = localStorage.getItem(terminalStatesStoreKey);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // Ignore
  }
  return {};
}

function persistHardwareStates(terminals: Record<string, TerminalEntry>) {
  try {
    const states: Record<string, TerminalStateEventLatestState> = {};
    for (const [id, entry] of Object.entries(terminals)) {
      states[id] = entry.hardwareState;
    }
    localStorage.setItem(terminalStatesStoreKey, JSON.stringify(states));
  } catch {
    // Ignore
  }
}

// === Store ===

interface TerminalStoreActions {
  setHardwareState(id: string, state: TerminalStateEventLatestState): void;
  setHardwareStates(states: Record<string, TerminalStateEventLatestState>): void;
  trackPayment(terminalId: string, paymentReference: string, cart?: CartDto): void;
  updatePaymentState(paymentReference: string, state: PaymentTrackingState, timestampMs: number): void;
  clearPayment(paymentReference: string): void;
  handleTerminalEvent(event: TerminalStateEvent): void;
  handlePaymentEvent(event: LocationPaymentEvent): void;
  reconcileFromPolling(terminals: TerminalWithLatestPaymentResponse[]): void;
  setSseState(state: SseConnectionState): void;
  subscribeToPayment(ref: string, cb: PaymentCallback): () => void;
  setOptimisticBusy(terminalId: string): void;
  clearOptimisticBusy(terminalId: string): void;
}

interface TerminalStoreState {
  terminals: Record<string, TerminalEntry>;
  optimisticBusy: Set<string>;
  sseState: SseConnectionState;
  actions: TerminalStoreActions;
}

function ensureTerminal(terminals: Record<string, TerminalEntry>, id: string): TerminalEntry {
  return (
    terminals[id] ?? { activePayment: null, hardwareState: TerminalStateEventLatestState.UNKNOWN, lastUpdatedMs: 0 }
  );
}

function findTerminalByPayment(terminals: Record<string, TerminalEntry>, ref: string): [string, TerminalEntry] | null {
  for (const [id, entry] of Object.entries(terminals)) {
    if (entry.activePayment?.paymentReference === ref) {
      return [id, entry];
    }
  }
  return null;
}

// Build initial terminal entries from persisted hardware states
const initialHardwareStates = loadPersistedHardwareStates();
const initialTerminals: Record<string, TerminalEntry> = {};
for (const [id, state] of Object.entries(initialHardwareStates)) {
  initialTerminals[id] = { activePayment: null, hardwareState: state, lastUpdatedMs: 0 };
}

export const useTerminalStore = create<TerminalStoreState>()(
  subscribeWithSelector((set, get) => ({
    actions: {
      clearOptimisticBusy(terminalId) {
        const timer = optimisticBusyTimers.get(terminalId);
        if (timer) {
          clearTimeout(timer);
          optimisticBusyTimers.delete(terminalId);
        }

        set((prev) => {
          if (!prev.optimisticBusy.has(terminalId)) return prev;
          const next = new Set(prev.optimisticBusy);
          next.delete(terminalId);
          return { optimisticBusy: next };
        });
      },

      clearPayment(paymentReference) {
        const found = findTerminalByPayment(get().terminals, paymentReference);
        if (!found) return;

        const [terminalId, entry] = found;
        set((prev) => ({
          terminals: {
            ...prev.terminals,
            [terminalId]: { ...entry, activePayment: null },
          },
        }));

        // Clean up subscribers
        paymentSubscribers.delete(paymentReference);
      },

      handlePaymentEvent(event) {
        const { pspTerminalId, paymentReference, state: rawState, timestampMs } = event;
        const state = mapLocationPaymentState(rawState);
        if (!state) return;

        const { terminals, actions } = get();

        // Ensure terminal + payment entry exists
        const existing = findTerminalByPayment(terminals, paymentReference);
        if (!existing) {
          // Don't auto-create for terminal states — the payment was already
          // cleared (e.g. by cancel) and there's nothing left to track.
          if (!isPaymentInProgress(state)) return;

          // Cross-device: auto-create tracked payment
          ensureTerminal(terminals, pspTerminalId);
          const payment: TrackedPayment = { paymentReference, terminalId: pspTerminalId, state, timestampMs };
          set((prev) => ({
            terminals: {
              ...prev.terminals,
              [pspTerminalId]: { ...ensureTerminal(prev.terminals, pspTerminalId), activePayment: payment },
            },
          }));
          notifySubscribers(paymentReference, state);
          return;
        }

        actions.updatePaymentState(paymentReference, state, timestampMs);
      },

      handleTerminalEvent(event) {
        const { pspTerminalId, latestState, timestampMs } = event;
        set((prev) => {
          const entry = ensureTerminal(prev.terminals, pspTerminalId);
          if (timestampMs < entry.lastUpdatedMs) return prev; // stale
          const next = {
            ...prev.terminals,
            [pspTerminalId]: { ...entry, hardwareState: latestState, lastUpdatedMs: timestampMs },
          };
          persistHardwareStates(next);
          return { terminals: next };
        });
      },

      reconcileFromPolling(polledTerminals) {
        const { terminals, actions } = get();
        const hardwareStates: Record<string, TerminalStateEventLatestState> = {};
        const knownRefs = new Set<string>();

        for (const terminal of polledTerminals) {
          // Same string enum values, different TS types
          hardwareStates[terminal.pspTerminalId] = terminal.latestState as unknown as TerminalStateEventLatestState;

          if (terminal.latestPayment) {
            const { paymentReference, latestState } = terminal.latestPayment;
            knownRefs.add(paymentReference);

            const trackingState = mapPollingPaymentState(latestState);
            if (!trackingState) continue;

            const existing = findTerminalByPayment(terminals, paymentReference);
            if (existing) {
              actions.updatePaymentState(paymentReference, trackingState, Date.now());
            } else {
              // Discovered by polling (cross-device or after reload)
              const payment: TrackedPayment = {
                paymentReference,
                terminalId: terminal.pspTerminalId,
                state: trackingState,
                timestampMs: Date.now(),
              };
              set((prev) => ({
                terminals: {
                  ...prev.terminals,
                  [terminal.pspTerminalId]: {
                    ...ensureTerminal(prev.terminals, terminal.pspTerminalId),
                    activePayment: payment,
                  },
                },
              }));
              notifySubscribers(paymentReference, trackingState);
            }
          }
        }

        actions.setHardwareStates(hardwareStates);

        // Clear stale payments and optimistic busy not known by any polled terminal
        for (const [id, entry] of Object.entries(get().terminals)) {
          if (hardwareStates[id] === undefined) continue; // not in polled set

          const ap = entry.activePayment;
          if (ap && !knownRefs.has(ap.paymentReference)) {
            const age = Date.now() - ap.timestampMs;
            if (age > TRACKED_PAYMENT_GRACE_MS) {
              actions.clearPayment(ap.paymentReference);
            }
          }

          if (!ap && get().optimisticBusy.has(id)) {
            actions.clearOptimisticBusy(id);
          }
        }
      },

      setHardwareState(id, state) {
        set((prev) => {
          const entry = ensureTerminal(prev.terminals, id);
          const next = { ...prev.terminals, [id]: { ...entry, hardwareState: state, lastUpdatedMs: Date.now() } };
          persistHardwareStates(next);
          return { terminals: next };
        });
      },

      setHardwareStates(states) {
        set((prev) => {
          const next = { ...prev.terminals };
          for (const [id, state] of Object.entries(states)) {
            const entry = ensureTerminal(next, id);
            next[id] = { ...entry, hardwareState: state, lastUpdatedMs: Date.now() };
          }
          persistHardwareStates(next);
          return { terminals: next };
        });
      },

      setOptimisticBusy(terminalId) {
        const existing = optimisticBusyTimers.get(terminalId);
        if (existing) clearTimeout(existing);

        const timer = setTimeout(() => {
          get().actions.clearOptimisticBusy(terminalId);
        }, OPTIMISTIC_BUSY_TIMEOUT_MS);
        optimisticBusyTimers.set(terminalId, timer);

        set((prev) => {
          const next = new Set(prev.optimisticBusy);
          next.add(terminalId);
          return { optimisticBusy: next };
        });
      },

      setSseState(state) {
        set({ sseState: state });
      },

      subscribeToPayment(ref, cb) {
        let subs = paymentSubscribers.get(ref);
        if (!subs) {
          subs = new Set();
          paymentSubscribers.set(ref, subs);
        }
        subs.add(cb);

        return () => {
          subs!.delete(cb);
          if (subs!.size === 0) {
            paymentSubscribers.delete(ref);
          }
        };
      },

      trackPayment(terminalId, paymentReference, cart) {
        get().actions.clearOptimisticBusy(terminalId);
        set((prev) => {
          const entry = ensureTerminal(prev.terminals, terminalId);
          const payment: TrackedPayment = {
            paymentReference,
            terminalId,
            state: 'INITIATING',
            timestampMs: Date.now(),
            cart,
          };
          return {
            terminals: { ...prev.terminals, [terminalId]: { ...entry, activePayment: payment } },
          };
        });
      },

      updatePaymentState(paymentReference, state, timestampMs) {
        const found = findTerminalByPayment(get().terminals, paymentReference);
        if (!found) return;

        const [terminalId, entry] = found;
        const current = entry.activePayment;
        if (!current) return;

        const currentOrdinal = STATE_ORDINAL[current.state];
        const nextOrdinal = STATE_ORDINAL[state];

        if (nextOrdinal < currentOrdinal) {
          return; // stale data from polling — expected race condition
        }

        if (current.state === state) return; // no-op

        const updatedPayment: TrackedPayment = { ...current, state, timestampMs };
        set((prev) => ({
          terminals: {
            ...prev.terminals,
            [terminalId]: { ...ensureTerminal(prev.terminals, terminalId), activePayment: updatedPayment },
          },
        }));

        notifySubscribers(paymentReference, state);
      },
    },
    optimisticBusy: new Set<string>(),
    sseState: 'disconnected',
    terminals: initialTerminals,
  }))
);

// === Hooks ===

export function useTerminalStatus(terminalId: string, hasActivePayment = false): TerminalDisplayState {
  const hasActiveRefund = useHasActiveRefund(terminalId);

  const displayState = useTerminalStore(
    useCallback(
      (s: TerminalStoreState) => {
        if (s.optimisticBusy.has(terminalId)) {
          return 'BUSY' as const;
        }

        const entry = s.terminals[terminalId];
        if (!entry) {
          return hasActivePayment ? ('BUSY' as const) : ('AVAILABLE' as const);
        }

        const ap = entry.activePayment;
        if (ap && isPaymentInProgress(ap.state)) {
          return 'BUSY' as const;
        }

        if (hasActivePayment) {
          return 'BUSY' as const;
        }

        return mapHardwareToDisplay(entry.hardwareState);
      },
      [terminalId, hasActivePayment]
    )
  );

  if (hasActiveRefund) {
    return 'BUSY';
  }
  return displayState;
}

export function useTrackedPayment(terminalId: string): TrackedPayment | null {
  return useTerminalStore(
    useCallback((s: TerminalStoreState) => s.terminals[terminalId]?.activePayment ?? null, [terminalId])
  );
}

export function useTerminalStoreActions() {
  return useTerminalStore((s) => s.actions);
}
